import AcUnitIcon from "@mui/icons-material/AcUnit";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import CategoryIcon from "@mui/icons-material/Category";
import ComputerIcon from "@mui/icons-material/Computer";
import ConstructionIcon from "@mui/icons-material/Construction";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import EngineeringIcon from "@mui/icons-material/Engineering";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PersonIcon from "@mui/icons-material/Person";
import PlumbingIcon from "@mui/icons-material/Plumbing";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { GET_VENDOR_TAXONOMY_URL } from "config/services";
import { searchNeevayNetwork } from "actions/searchActions";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import axios from "utils/axios";
import searchService from "services/searchService";
import { buildVendorCategoryFilter, findTaxonomyMatch } from "../utils/vendorCategorySearch";

const CATEGORY_MAP = {
  "Turnkey Contractors": "Contractor (Providing and Fixing)",
  "Labour Contractors": "Labour Contractor",
  "Material Suppliers": "Material Supplier",
};
const MOST_POPULAR_SEARCH_CITIES = ["Pune", "Mumbai", "Delhi", "Bengaluru"];
const EMPTY_DISCIPLINE_FILTERS = {
  contractor: "",
  material: "",
};

const normalizeName = (value = "") => value.toString().trim().toLowerCase();

const sortAlphabetically = (values = []) =>
  [...values].sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: "base" }),
  );


const EMPTY_VENDOR_CATEGORY_COUNTS = {
  type: {},
  discipline: {},
  category: {},
  subCategory: {},
};

const getCountFromFacet = (facetField, id) => {
  if (!id || !facetField) return 0;
  const key = String(id);

  if (Array.isArray(facetField)) {
    for (let index = 0; index < facetField.length; index += 2) {
      if (String(facetField[index]) === key) {
        return Number(facetField[index + 1]) || 0;
      }
    }
  }

  if (typeof facetField === "object") {
    return Number(facetField[key]) || 0;
  }

  return 0;
};

const buildVendorCategoryCounts = (facetFields = {}) => ({
  type: facetFields["vendorCategories.vendorType"] || facetFields.vendorCategoryTypes_str || {},
  discipline: facetFields["vendorCategories.disciplines.disciplineId"] || {},
  category: facetFields["vendorCategories.disciplines.categories.categoryId"] || {},
  subCategory: facetFields["vendorCategories.disciplines.categories.subCategories.subCategoryId"] || {},
});
const getDisciplineIcon = (disciplineName = "") => {
  const name = disciplineName.toLowerCase();

  if (name.includes("civil") || name.includes("architect") || name.includes("structure")) {
    return ArchitectureIcon;
  }

  if (name.includes("specialised") || name.includes("specialized")) {
    return WorkspacePremiumIcon;
  }

  if (name.includes("consultant") || name.includes("consultancy")) {
    return PersonIcon;
  }

  if (name.includes("electric") || name.includes("power") || name.includes("solar")) {
    return ElectricalServicesIcon;
  }

  if (name.includes("transport") || name.includes("logistic") || name.includes("truck")) {
    return LocalShippingIcon;
  }

  if (name.includes("it") || name.includes("hardware") || name.includes("software")) {
    return ComputerIcon;
  }

  if (name.includes("plumb") || name.includes("water") || name.includes("pipe")) {
    return PlumbingIcon;
  }

  if (name.includes("hvac") || name.includes("air") || name.includes("ac")) {
    return AcUnitIcon;
  }

  if (name.includes("fire")) {
    return LocalFireDepartmentIcon;
  }

  if (name.includes("mechanical") || name.includes("engineer")) {
    return EngineeringIcon;
  }

  if (name.includes("contract") || name.includes("construct")) {
    return ConstructionIcon;
  }

  return CategoryIcon;
};

const buildDisciplineGroups = (
  taxonomyData = {},
  vendorCategoryCounts = EMPTY_VENDOR_CATEGORY_COUNTS,
) => {
  const buildGroup = (mainCategory) =>
    (taxonomyData?.[mainCategory]?.disciplines || [])
      .map((discipline) => {
        const itemNames = new Set();
        const disciplineId = discipline?._id ? String(discipline._id) : "";

        (discipline?.categories || []).forEach((category) => {
          if (category?.name) {
            itemNames.add(normalizeName(category.name));
          }

          (category?.subCategories || []).forEach((subCategory) => {
            if (subCategory?.name) {
              itemNames.add(normalizeName(subCategory.name));
            }
          });
        });

        return {
          key: normalizeName(discipline?.name),
          id: disciplineId,
          name: discipline?.name || "Miscellaneous",
          vendorCount: getCountFromFacet(vendorCategoryCounts.discipline, disciplineId),
          categories: (discipline?.categories || [])
            .map((category) => ({
              id: category?._id ? String(category._id) : "",
              name: category?.name || "Miscellaneous",
              vendorCount: getCountFromFacet(vendorCategoryCounts.category, category?._id),
              subCategories: (category?.subCategories || [])
                .map((subCategory) => ({
                  id: subCategory?._id ? String(subCategory._id) : "",
                  name: subCategory?.name || "Miscellaneous",
                  vendorCount: getCountFromFacet(vendorCategoryCounts.subCategory, subCategory?._id),
                }))
                .filter((subCategory) => Number(subCategory.vendorCount) > 0),
            }))
            .filter((category) => Number(category.vendorCount) > 0),
          itemNames: [...itemNames],
        };
      })
      .filter((discipline) => discipline.name && Number(discipline.vendorCount) > 0);

  return {
    contractor: buildGroup("Contractor"),
    material: buildGroup("Material"),
  };
};

const filterItemsByDiscipline = (items = [], disciplineKey = "", disciplines = []) => {
  if (!disciplineKey) return items;

  const matchedDiscipline = disciplines.find((discipline) => discipline.key === disciplineKey);
  if (!matchedDiscipline) return items;

  const allowedNames = new Set(matchedDiscipline.itemNames);
  return items.filter((item) => allowedNames.has(normalizeName(item)));
};

const SearchCategoryTable = ({
  allowedCategoryType = "all",
  onClose,
  sliderName = "",
}) => {
  const [categoriesData, setCategoriesData] = useState({});
  const [vendorTaxonomyData, setVendorTaxonomyData] = useState({});
  const [disciplineGroups, setDisciplineGroups] = useState({
    contractor: [],
    material: [],
  });
  const [vendorCategoryCounts, setVendorCategoryCounts] = useState(
    EMPTY_VENDOR_CATEGORY_COUNTS,
  );
  const [regions, setRegions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [pendingTaxonomySearch, setPendingTaxonomySearch] = useState(null);
  const [citySearch, setCitySearch] = useState("");
  const [filteredCities, setFilteredCities] = useState([]);
  const [columnCount, setColumnCount] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleCities, setVisibleCities] = useState(10);
  const [activeDisciplines, setActiveDisciplines] = useState(
    EMPTY_DISCIPLINE_FILTERS,
  );
  const [activeCategories, setActiveCategories] = useState(
    EMPTY_DISCIPLINE_FILTERS,
  );
  const lastInteractionRef = useRef({ type: "", level: "" });
  const categoryPanelRef = useRef(null);
  const subCategoryPanelRef = useRef(null);
  const location = useLocation();
  const dispatch = useDispatch();
  const cities = useSelector((state) => state.regions.cities);
  const subCategoryLinkClass =
    "block w-full cursor-pointer rounded-xl border border-[#F3E3D8] bg-[#FFFDFC] px-2.5 py-1.5 text-left text-xs font-medium text-[#555548] transition-all duration-150 hover:border-[#F5B99A] hover:bg-[#FFF4EE] hover:text-[#B43A12] hover:shadow-sm";

  const calculateColumnCount = () => {
    const width = window.innerWidth;
    if (width < 768) return 1;
    if (width < 1024) return 4;
    if (width < 1280) return 5;
    return 6;
  };

  useEffect(() => {
    const handleResize = () => {
      setColumnCount(calculateColumnCount());
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCategorySelect = (displayName, subCategory, city) => {
    const apiCategoryName = CATEGORY_MAP[displayName];
    const categoryType = displayName === "Material Suppliers" ? "material" : "contractor";
    const taxonomyMatch = findTaxonomyMatch(vendorTaxonomyData, categoryType, subCategory);
    const taxonomyFilter = taxonomyMatch
      ? buildVendorCategoryFilter({ type: categoryType, ...taxonomyMatch })
      : "";
    const categoryFilter = `businessProfile.businessCategory_str:("${apiCategoryName}")`;
    const subCategoryFilter = `businessProfile.subCategory_str:("${encodeURIComponent(subCategory)}")`;
    const filterStr = taxonomyFilter || `${categoryFilter} AND ${subCategoryFilter}`;

    dispatch(
      searchNeevayNetwork({
        searchKeyWord: taxonomyFilter ? "" : subCategory,
        solrSearchKeyWord: taxonomyFilter ? "" : undefined,
        regionString: city,
        filterStr,
        facetTitle: "Category",
        facetValue: apiCategoryName,
        isSelected: true,
        subCategory,
      }),
    );

    const params = new URLSearchParams({
      searchKey: subCategory,
      city,
      category: displayName,
    });

    if (sliderName !== "NN") {
      const newUrl = `/neevay-network?${params.toString()}`;
      window.history.pushState({}, "", newUrl);

      setSelectedCategory(null);
      setSelectedSubCategory(null);
      setCitySearch("");
    }

    setTimeout(() => {
      if (onClose) onClose();
    }, 500);
  };

  const handleSubCategoryClick = (displayName, subCategory) => {
    const originalCategory =
      displayName === "Material Suppliers"
        ? "Material Suppliers"
        : categoriesData["Turnkey Contractors"]?.includes(subCategory)
          ? "Turnkey Contractors"
          : "Labour Contractors";

    setSelectedCategory(originalCategory);
    setSelectedSubCategory(subCategory);
    setCitySearch("");
    setVisibleCities(10);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchCategoryBrowseData = async () => {
      setIsLoading(true);

      const [subcategoriesResult, taxonomyResult, searchFacetResult] = await Promise.allSettled([
        axios.get("/api/supplier/mgmt/subcategories"),
        axios.get(GET_VENDOR_TAXONOMY_URL),
        searchService.getVendorCategoryCounts(),
      ]);

      if (!isMounted) return;

      if (subcategoriesResult.status === "fulfilled") {
        const data = subcategoriesResult.value?.data?.data || [];
        const mappedData = {};

        Object.entries(CATEGORY_MAP).forEach(([displayName, apiCategoryName]) => {
          const categoryObj = data.find((category) => category.category === apiCategoryName);
          if (categoryObj) {
            mappedData[displayName] = sortAlphabetically(categoryObj.subCategories || []);
          }
        });

        setCategoriesData(mappedData);
      } else {
        console.error("Failed to fetch subcategories", subcategoriesResult.reason);
      }

      const nextVendorCategoryCounts =
        searchFacetResult.status === "fulfilled"
          ? buildVendorCategoryCounts(
              searchFacetResult.value?.facet_counts?.facet_fields,
            )
          : EMPTY_VENDOR_CATEGORY_COUNTS;

      setVendorCategoryCounts(nextVendorCategoryCounts);

      if (taxonomyResult.status === "fulfilled") {
        const taxonomyData = taxonomyResult.value?.data?.data || {};
        setVendorTaxonomyData(taxonomyData);
        setDisciplineGroups(buildDisciplineGroups(taxonomyData, nextVendorCategoryCounts));
      } else {
        console.error("Failed to fetch vendor taxonomy", taxonomyResult.reason);
        setDisciplineGroups({
          contractor: [],
          material: [],
        });
      }

      setIsLoading(false);
    };

    fetchCategoryBrowseData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const initialRegions = [...new Set([...MOST_POPULAR_SEARCH_CITIES, ...cities])];
    setRegions(initialRegions);
    setFilteredCities(initialRegions);
  }, [cities]);

  useEffect(() => {
    if (citySearch.trim() === "") {
      setFilteredCities(regions);
    } else {
      const filtered = regions.filter((city) =>
        city.toLowerCase().includes(citySearch.toLowerCase()),
      );
      setFilteredCities(filtered);
    }
  }, [citySearch, regions]);

  const handleCitySearchChange = (event) => {
    setCitySearch(event.target.value);
  };

  const handleResetSelections = () => {
    setPendingTaxonomySearch(null);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setCitySearch("");
  };

  const contractorSubcategories = sortAlphabetically([
    ...new Set([
      ...(categoriesData["Turnkey Contractors"] || []),
      ...(categoriesData["Labour Contractors"] || []),
    ]),
  ]);

  const materialSubcategories = sortAlphabetically(
    categoriesData["Material Suppliers"] || [],
  );

  const filteredContractorSubcategories = filterItemsByDiscipline(
    contractorSubcategories,
    activeDisciplines.contractor,
    disciplineGroups.contractor,
  );

  const filteredMaterialSubcategories = filterItemsByDiscipline(
    materialSubcategories,
    activeDisciplines.material,
    disciplineGroups.material,
  );

  const distributeItemsColumnMajor = (items, columns) => {
    const result = Array.from({ length: columns }, () => []);
    const itemsPerColumn = Math.ceil(items.length / columns);

    for (let index = 0; index < items.length; index += 1) {
      const columnIndex = Math.floor(index / itemsPerColumn);
      if (columnIndex < columns) {
        result[columnIndex].push(items[index]);
      }
    }

    return result;
  };

  const LoadingSpinner = () => (
    <div className="flex min-h-[240px] w-full items-center justify-center py-8">
      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-orange-500" />
    </div>
  );

  const handleDisciplineToggle = (type, disciplineKey) => {
    lastInteractionRef.current = { type, level: "category" };
    setActiveDisciplines((prev) => ({
      ...prev,
      [type]: prev[type] === disciplineKey ? "" : disciplineKey,
    }));
    setActiveCategories((prev) => ({ ...prev, [type]: "" }));
  };

  const handleCategoryToggle = (type, categoryId) => {
    lastInteractionRef.current = { type, level: "subCategory" };
    setActiveCategories((prev) => ({
      ...prev,
      [type]: prev[type] === categoryId ? "" : categoryId,
    }));
  };

  const getTypeSearchConfig = (type) => {
    if (type === "material") {
      return {
        categoryType: "Material",
        facetValue: "Material Supplier",
        facetValues: ["Material Supplier"],
        vendorFilter: 'businessProfile.businessCategory_str:("Material Supplier")',
      };
    }

    return {
      categoryType: "Contractor",
      facetValue: "Contractor (Providing and Fixing)",
      facetValues: ["Contractor (Providing and Fixing)", "Labour Contractor"],
      vendorFilter: 'businessProfile.businessCategory_str:("Contractor (Providing and Fixing)" OR "Labour Contractor")',
    };
  };

  const executeTaxonomySearch = ({ type, discipline, category, subCategory }, city) => {
    const config = getTypeSearchConfig(type);
    const taxonomyFilter = buildVendorCategoryFilter({
      type: config.categoryType,
      disciplineId: discipline?.id,
      categoryId: category?.id,
      subCategoryId: subCategory?.id,
    });
    const filterStr = taxonomyFilter || config.vendorFilter;
    const searchLabel = subCategory?.name || category?.name || discipline?.name || config.categoryType;

    dispatch(
      searchNeevayNetwork({
        searchKeyWord: taxonomyFilter ? "" : searchLabel,
        solrSearchKeyWord: taxonomyFilter ? "" : undefined,
        regionString: city,
        filterStr,
        facetTitle: "Category",
        facetValue: config.facetValue,
        facetValues: config.facetValues,
        isSelected: true,
        subCategory: searchLabel,
      }),
    );

    if (sliderName !== "NN") {
      const params = new URLSearchParams({
        searchKey: searchLabel,
        city,
        category: config.categoryType,
        type,
      });

      if (discipline?.id) params.set("disciplineId", discipline.id);
      if (category?.id) params.set("categoryId", category.id);
      if (subCategory?.id) params.set("subCategoryId", subCategory.id);

      window.history.pushState({}, "", `/neevay-network?${params.toString()}`);
    }

    setPendingTaxonomySearch(null);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setCitySearch("");

    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  const handleVendorCountSearch = ({ type, discipline, category, subCategory }) => {
    const config = getTypeSearchConfig(type);
    const searchLabel = subCategory?.name || category?.name || discipline?.name || config.categoryType;

    setPendingTaxonomySearch({ type, discipline, category, subCategory });
    setSelectedCategory(type === "material" ? "Material Suppliers" : "Turnkey Contractors");
    setSelectedSubCategory(searchLabel);
    setCitySearch("");
    setVisibleCities(10);
  };

  const renderCountButton = ({ count, onClick }) => {
    const vendorCount = Number(count) || 0;
    const isDisabled = vendorCount <= 0;

    return (
      <span
        role={isDisabled ? undefined : "button"}
        tabIndex={isDisabled ? -1 : 0}
        onClick={(event) => {
          event.stopPropagation();
          if (!isDisabled) onClick();
        }}
        onKeyDown={(event) => {
          if (isDisabled) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            onClick();
          }
        }}
        className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-semibold transition ${isDisabled
          ? "cursor-not-allowed border-[#E4E7EE] bg-[#F4F4EF] text-[#A1A196]"
          : "cursor-pointer border-[#F7B99D] bg-[#FFF0E8] text-[#C2410C] underline decoration-dotted underline-offset-2 hover:bg-[#FD6600] hover:text-white hover:no-underline"
        }`}
        title={isDisabled ? "No vendors available" : "View vendors"}
      >
        {vendorCount} {vendorCount === 1 ? "vendor" : "vendors"}
        {!isDisabled && <span aria-hidden="true">-&gt;</span>}
      </span>
    );
  };

  const getTopLevelVendorCount = (type) => {
    const categoryType = type === "material" ? "Material" : "Contractor";
    const directCount = getCountFromFacet(vendorCategoryCounts.type, categoryType);

    if (directCount) return directCount;

    return (disciplineGroups[type] || []).reduce(
      (total, discipline) => total + (Number(discipline.vendorCount) || 0),
      0,
    );
  };
  const renderDisciplineFilters = (type) => {
    const disciplines = disciplineGroups[type];
    if (!disciplines.length) return null;

    const selectedDisciplineKey = activeDisciplines[type];
    const selectedDiscipline = disciplines.find((discipline) => discipline.key === selectedDisciplineKey);
    const selectedCategoryId = activeCategories[type];

    return (
      <div className="mb-3 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7A7D86]">
            Discipline / Category / Subcategory
          </p>
          {(selectedDisciplineKey || selectedCategoryId) && (
            <button
              type="button"
              onClick={() => {
                setActiveDisciplines((prev) => ({ ...prev, [type]: "" }));
                setActiveCategories((prev) => ({ ...prev, [type]: "" }));
              }}
              className="rounded-full border border-[#E4E7EE] px-2.5 py-1 text-[11px] font-semibold text-[#6B6E76] transition-colors hover:border-[#F7B99D] hover:bg-[#FFF4EE] hover:text-[#B43A12]"
            >
              Clear selection
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {disciplines.map((discipline) => {
            const Icon = getDisciplineIcon(discipline.name);
            const isSelected = selectedDisciplineKey === discipline.key;

            return (
              <button
                key={discipline.key}
                type="button"
                onClick={() => handleDisciplineToggle(type, discipline.key)}
                className={`relative flex min-w-0 items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all duration-200 ${
                  isSelected
                    ? "border-orange-500 bg-orange-50 text-[#1E293B] shadow-[0_10px_24px_rgba(253,102,0,0.14)] ring-1 ring-orange-200"
                    : "border-[#E4E7EE] bg-white text-[#30343D] hover:border-[#F7B99D] hover:bg-[#FFF4EE]"
                }`}
              >
                <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isSelected ? "bg-white text-orange-500" : "bg-orange-50 text-[#5F5B4E]"}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1 pr-2">
                  <span className="block truncate text-sm font-semibold underline decoration-dotted underline-offset-4 hover:text-[#B43A12]">
                    {discipline.name}
                  </span>
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDisciplineToggle(type, discipline.key);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      event.stopPropagation();
                      handleDisciplineToggle(type, discipline.key);
                    }
                  }}
                  className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full border border-[#F7B99D] bg-[#FFF0E8] px-2.5 py-1 text-[10px] font-semibold text-[#C2410C] underline decoration-dotted underline-offset-2 transition hover:bg-[#FD6600] hover:text-white hover:no-underline"
                  title="Open categories"
                >
                  {Number(discipline.vendorCount) || 0} {(Number(discipline.vendorCount) || 0) === 1 ? "vendor" : "vendors"}
                </span>
              </button>
            );
          })}
        </div>

        {selectedDiscipline && (
          <div ref={lastInteractionRef.current.type === type ? categoryPanelRef : null} className="rounded-xl border border-[#F3E3D8] border-l-4 border-l-[#FD6600] bg-[linear-gradient(135deg,#FFF7F0_0%,#FFFDFC_72%)] p-3 shadow-[0_16px_34px_rgba(180,58,18,0.12)] ring-1 ring-orange-100" style={{ animation: "taxonomyReveal 360ms cubic-bezier(0.16, 1, 0.3, 1)" }}>
            <div className="mb-2 flex flex-wrap items-center gap-1 text-[11px] font-semibold text-[#7A7D86]">
              <span>{type === "material" ? "Material" : "Contractor"}</span>
              <span>/</span>
              <span>{selectedDiscipline.name}</span>
            </div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-[#30343D]">
                Categories under <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[#C2410C]">{selectedDiscipline.name}</span>
              </p>
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
              {(selectedDiscipline.categories || []).map((category) => {
                const isCategorySelected = selectedCategoryId === category.id;
                return (
                  <React.Fragment key={category.id || category.name}>
                    <button
                      type="button"
                      onClick={() => handleCategoryToggle(type, category.id)}
                      className={`relative flex min-w-0 items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-all duration-200 ${
                        isCategorySelected
                          ? "border-orange-500 bg-orange-50 text-[#1E293B] shadow-[0_10px_24px_rgba(253,102,0,0.14)] ring-1 ring-orange-200"
                          : "border-[#E4E7EE] bg-white text-[#30343D] hover:border-[#F7B99D] hover:bg-[#FFF4EE]"
                      }`}
                    >
                      <span className="min-w-0 truncate font-semibold underline decoration-dotted underline-offset-4 hover:text-[#B43A12]">{category.name}</span>
                      {renderCountButton({
                        count: category.vendorCount,
                        onClick: () => handleVendorCountSearch({ type, discipline: selectedDiscipline, category }),
                      })}
                    </button>

                    {isCategorySelected && (
                      <div ref={lastInteractionRef.current.type === type ? subCategoryPanelRef : null} className="rounded-xl border border-[#F3E3D8] border-l-4 border-l-[#FD6600] bg-[linear-gradient(135deg,#FFFFFF_0%,#FFF7F0_100%)] p-3 shadow-[0_16px_34px_rgba(180,58,18,0.12)] ring-1 ring-orange-100 md:col-span-2 xl:col-span-3" style={{ animation: "taxonomyReveal 360ms cubic-bezier(0.16, 1, 0.3, 1)" }}>
                        <div className="mb-2 flex flex-wrap items-center gap-1 text-[11px] font-semibold text-[#7A7D86]">
                          <span>{type === "material" ? "Material" : "Contractor"}</span>
                          <span>/</span>
                          <span>{selectedDiscipline.name}</span>
                          <span>/</span>
                          <span>{category.name}</span>
                        </div>
                        <p className="mb-2 text-xs font-semibold text-[#30343D]">
                          Subcategories under <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[#C2410C]">{category.name}</span>
                        </p>
                        {category.subCategories?.length ? (
                          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
                            {category.subCategories.map((subCategory) => (
                              <div key={subCategory.id || subCategory.name} className="flex min-w-0 items-center justify-between gap-2 rounded-lg border border-[#E4E7EE] bg-[#FFFDFC] px-3 py-2 text-xs">
                                <span className="min-w-0 truncate font-semibold text-[#30343D] underline decoration-dotted underline-offset-4 hover:text-[#B43A12]">{subCategory.name}</span>
                                {renderCountButton({
                                  count: subCategory.vendorCount,
                                  onClick: () => handleVendorCountSearch({ type, discipline: selectedDiscipline, category, subCategory }),
                                })}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="rounded-lg border border-dashed border-[#F1D1C2] bg-[#FFF9F5] px-3 py-3 text-xs text-[#8A6A5A]">
                            No subcategories found for this category.
                          </div>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (sliderName === "NN") return;

    if (location.pathname === "/neevay-network") {
      const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : location.search);
      const shouldRestoreSelection = params.get("restoreSelection") === "1";
      const searchKey = params.get("searchKey")?.replace(/-/g, " ");
      const city = params.get("city")?.replace(/-/g, " ");
      const category = params.get("category")?.replace(/-/g, " ");
      const restoreType = params.get("type");
      const restoreDisciplineId = params.get("disciplineId");
      const restoreCategoryId = params.get("categoryId");

      if (shouldRestoreSelection && restoreType) {
        const restoreDisciplines = disciplineGroups[restoreType] || [];
        const matchedDiscipline = restoreDisciplines.find(
          (discipline) => discipline.id === restoreDisciplineId || discipline.key === restoreDisciplineId,
        );

        if (restoreDisciplineId && matchedDiscipline) {
          setActiveDisciplines((prev) => ({ ...prev, [restoreType]: matchedDiscipline.key }));
        }
        if (restoreCategoryId) {
          setActiveCategories((prev) => ({ ...prev, [restoreType]: restoreCategoryId }));
        }
        return;
      }

      if (searchKey && city && category) {
        const apiCategoryName = CATEGORY_MAP[category];
        if (apiCategoryName) {
          const categoryType = category === "Material Suppliers" ? "material" : "contractor";
          const taxonomyMatch = findTaxonomyMatch(vendorTaxonomyData, categoryType, searchKey);
          const taxonomyFilter = taxonomyMatch
            ? buildVendorCategoryFilter({ type: categoryType, ...taxonomyMatch })
            : "";
          const categoryFilter = `businessProfile.businessCategory_str:("${apiCategoryName}")`;
          const subCategoryFilter = `businessProfile.subCategory_str:("${encodeURIComponent(searchKey)}")`;
          const filterStr = taxonomyFilter || `${categoryFilter} AND ${subCategoryFilter}`;

          dispatch(
            searchNeevayNetwork({
              searchKeyWord: taxonomyFilter ? "" : searchKey,
              solrSearchKeyWord: taxonomyFilter ? "" : undefined,
              regionString: city,
              filterStr,
              facetTitle: "Category",
              facetValue: apiCategoryName,
              isSelected: true,
              subCategory: searchKey,
            }),
          );
        }
      }
    }
  }, [location.search, location.pathname, dispatch, sliderName, vendorTaxonomyData]);

  useEffect(() => {
    if (onClose) {
      setSelectedCategory(null);
      setSelectedSubCategory(null);
      setCitySearch("");
    }
  }, [onClose]);

  useEffect(() => {
    setActiveDisciplines((prev) => ({
      contractor: disciplineGroups.contractor.some(
        (discipline) => discipline.key === prev.contractor,
      )
        ? prev.contractor
        : "",
      material: disciplineGroups.material.some(
        (discipline) => discipline.key === prev.material,
      )
        ? prev.material
        : "",
    }));
  }, [disciplineGroups]);

  useEffect(() => {
    const target = lastInteractionRef.current.level === "subCategory"
      ? subCategoryPanelRef.current
      : categoryPanelRef.current;

    if (!target) return undefined;

    const timer = setTimeout(() => {
      const scrollContainer = target.closest(".overflow-y-auto");
      const targetRect = target.getBoundingClientRect();
      const viewportRect = scrollContainer
        ? scrollContainer.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight };
      const isFullyVisible =
        targetRect.top >= viewportRect.top + 12 &&
        targetRect.bottom <= viewportRect.bottom - 12;

      if (!isFullyVisible) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 80);

    return () => clearTimeout(timer);
  }, [
    activeDisciplines.contractor,
    activeDisciplines.material,
    activeCategories.contractor,
    activeCategories.material,
  ]);
  const showContractorCategories = allowedCategoryType === "all" || allowedCategoryType === "none" || allowedCategoryType === "contractor";
  const showMaterialCategories = allowedCategoryType === "all" || allowedCategoryType === "none" || allowedCategoryType === "material";

  const locationPopup =
    selectedSubCategory && typeof document !== "undefined"
      ? createPortal(
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(31,30,41,0.18)] px-4 backdrop-blur-[2px]">
            <div className="w-full max-w-md rounded-2xl border border-[#F1D5C6] bg-[linear-gradient(180deg,#FFF9F5_0%,#FFFFFF_38%)] p-3.5 shadow-xl">
              <div className="mb-2.5">
                <div className="flex items-center justify-between">
                  <div className="mb-2 flex space-x-1">
                    <h3 className="text-xs font-medium text-gray-600">
                      Search in{" "}
                      <span className="text-sm font-semibold text-[#B43A12]">
                        {selectedSubCategory}{" "}
                        <span className="text-xs font-medium text-gray-600">
                          (
                          {selectedCategory === "Material Suppliers"
                            ? "Material"
                            : "Contractor"}
                          )
                        </span>
                      </span>
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={handleResetSelections}
                    className="text-lg text-gray-500 hover:text-gray-700"
                  >
                    x
                  </button>
                </div>

                <input
                  type="text"
                  placeholder="Search for your city..."
                  value={citySearch}
                  onChange={handleCitySearchChange}
                  className="mb-2 max-h-9 w-full rounded-lg border border-[#F0D8CA] bg-white px-3 py-2 text-xs focus:border-[#E95420] focus:outline-none focus:ring-0"
                />

                <div>
                  {filteredCities.length > 0 ? (
                    <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto">
                      {filteredCities.slice(0, visibleCities).map((city, index) => (
                        <button
                          type="button"
                          key={index}
                          className="cursor-pointer rounded-full border border-[#F0D8CA] bg-white px-3 py-1 text-center text-xs font-medium text-[#555548] transition-colors hover:border-[#F7B99D] hover:bg-[#FFF4EE] hover:text-[#B43A12]"
                          onClick={() => {
                            if (pendingTaxonomySearch) {
                              executeTaxonomySearch(pendingTaxonomySearch, city);
                              return;
                            }
                            handleCategorySelect(selectedCategory, selectedSubCategory, city);
                          }}
                        >
                          {city}
                        </button>
                      ))}
                      {filteredCities.length > visibleCities && (
                        <div
                          className="cursor-pointer text-xs font-semibold text-[#E95420]"
                          onClick={() => setVisibleCities((prev) => prev + 20)}
                        >
                          + {filteredCities.length - visibleCities} more cities
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="pt-1 text-center text-xs italic text-gray-500">
                      No cities found matching your search
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative bg-transparent">
      <style>{`@keyframes taxonomyReveal { from { opacity: 0; transform: translateY(14px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>
      {locationPopup}

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="max-h-[calc(100vh-100px)] min-h-0 space-y-4 overflow-y-auto overscroll-contain bg-transparent p-4 pr-2">

          {showContractorCategories && (
            <div className="overflow-hidden rounded-md border border-[#F0D7C8] bg-white/95 shadow-[0_18px_40px_rgba(180,58,18,0.08)]">
              <div className="flex items-start justify-between gap-3 border-b border-[#F3DED3] bg-[linear-gradient(135deg,#FFF5EE_0%,#FFFDFB_72%,#F9F2FF_100%)] px-3.5 py-3 text-left">
                <div>
                  <h3 className="text-sm font-semibold text-[#14161B]">
                    Contractor - Activity
                  </h3>
                  <p className="mt-1 text-xs text-[#8A6A5A]">
                    Browse work categories and then choose the city to search vendors.
                  </p>
                </div>
                <span className="rounded-full border border-[#F7D9C7] bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-[#D35B1D]">
                  {getTopLevelVendorCount("contractor")} vendors
                </span>
              </div>

              <div className="px-3.5 py-3.5">
                {renderDisciplineFilters("contractor")}
              </div>
            </div>
          )}

          {showMaterialCategories && (
            <div className="overflow-hidden rounded-2xl border border-[#F0D7C8] bg-white/95 shadow-[0_18px_40px_rgba(180,58,18,0.08)]">
              <div className="flex items-start justify-between gap-3 border-b border-[#F3DED3] bg-[linear-gradient(135deg,#FFF7F0_0%,#FFFDFB_68%,#EEF5FF_100%)] px-3.5 py-3 text-left">
                <div>
                  <h3 className="text-sm font-semibold text-[#14161B]">
                    Material - Category
                  </h3>
                  <p className="mt-1 text-xs text-[#8A6A5A]">
                    Browse material categories and then select the city to continue.
                  </p>
                </div>
                <span className="rounded-full border border-[#F7D9C7] bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-[#D35B1D]">
                  {getTopLevelVendorCount("material")} vendors
                </span>
              </div>

              <div className="px-3.5 py-3.5">
                {renderDisciplineFilters("material")}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchCategoryTable;


















